import {
  ChangeDetectionStrategy,
  Component,
  ErrorHandler,
  Input,
  TemplateRef,
  inject,
} from '@angular/core';
import { FaceFilterType } from '@freelancer/components/face-filter';
import { MediaDeviceErrorCode } from '@freelancer/media-devices';
import { ModalRef } from '@freelancer/ui';
import {
  IconBackdrop,
  IconBackdropStyle,
  IconColor,
  IconSize,
} from '@freelancer/ui/icon';
import { ModalColor } from '@freelancer/ui/modal';

@Component({
  standalone: false,
  template: `
    <ng-container *flModalTitle>
      {{ modalTitle }}
    </ng-container>

    <div flTrackingSection="AppWebcamVideo">
      <!--
      Custom close button implementation:
    
      Before, modal has a closeOnBackdropClick feature implemented in D102094. Recent iteration of
      the modal does not include this feature anymore, as it was replaced by the closeable parameter
      in D122185, However, the closeable feature has its limitations. Specifically, either you can
      close the modal or not with no in-between options.
    
      This approach enables:
      1. Independent control of close button visibility with showCloseButton for video record modal
      2. Consistent styling and behavior with the standard modal implementation's close button
      -->
      @if (showCloseButton) {
        <fl-icon
          class="VideoRecordModalCloseButton"
          label="Close Video Record Modal"
          i18n-label="Close Video Record Modal clickable icon"
          [attr.data-color]="color"
          [color]="IconColor.FOREGROUND"
          [name]="'ui-close'"
          [backdrop]="IconBackdrop.PRIMARY"
          [backdropStyle]="IconBackdropStyle.TRANSPARENT"
          [hoverAnimation]="'spin-and-fade'"
          [size]="IconSize.MID"
          [clickable]="true"
          [id]="'videoRecordModalCloseButton'"
          [class.mobile:hide]="mobileFullscreen"
          (click)="closeModal()"
          [useIconFont]="true"
        ></fl-icon>
      }

      @if (this.modalHeader) {
        <h4 class="text-body-20 VideoRecordModal-header mb-small">
          {{ modalHeader }}
        </h4>
      }

      @if (this.content) {
        <div class="VideoRecordModal-content">
          <ng-container [ngTemplateOutlet]="content"></ng-container>
        </div>
      }

      <fl-video-record
        [faceFilterType]="faceFilterType"
        [subContent]="subContent"
        [contentWhileRecording]="contentWhileRecording"
        (onError)="onError($event)"
      ></fl-video-record>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./video-record-modal.component.scss'],
})
export class VideoRecordModalComponent {
  private errorHandler = inject(ErrorHandler);
  private modalRef = inject<ModalRef<VideoRecordModalComponent>>(ModalRef);

  IconColor = IconColor;
  IconSize = IconSize;
  IconBackdrop = IconBackdrop;
  IconBackdropStyle = IconBackdropStyle;

  @Input() modalHeader?: string;
  @Input() modalTitle?: string;
  @Input() content?: TemplateRef<HTMLElement>;
  @Input() subContent?: TemplateRef<HTMLElement>;
  @Input() contentWhileRecording?: TemplateRef<HTMLElement>;
  @Input() faceFilterType: FaceFilterType = 'none';
  @Input() showCloseButton = true;
  @Input() color = ModalColor.LIGHT;
  @Input() mobileFullscreen = false;

  closeModal(): void {
    this.modalRef.close();
  }

  onError(error: MediaDeviceErrorCode): void {
    if (error === MediaDeviceErrorCode.UNSUPPORTED_BROWSER) {
      this.modalRef.setError(
        new Error(
          `The specified MIME type is not supported by the user agent.`,
        ),
      );
    } else {
      const e = new Error(`Unknown error occured: ${error}`);
      this.errorHandler.handleError(e);
      this.modalRef.setError(e);
    }
  }
}
